import httpClient from '@/httpClient'
import {
  GetTabsResponseSchema,
  GetTabRequestSchema,
  CreateTabRequestSchema,
  EditTabRequestSchema,
  DeleteTabRequestSchema,
  GetAllCardsRequestSchema,
  GetCardRequestSchema,
  CreateCardRequestSchema,
  EditCardRequestSchema,
  DeleteCardRequestSchema,
  ViewCardRequestSchema,
  GetAllCardsResponseSchema,
  GetCardResponseSchema,
  ViewCardResponseSchema,
  CreateTabResponseSchema,
  EditTabResponseSchema,
  DeleteTabResponseSchema,
  CreateCardResponseSchema,
  EditCardResponseSchema,
  DeleteCardResponseSchema,
  type GetTabsResponse,
  type GetTabsResponseInput,
  type GetTabRequest,
  type GetTabResponse,
  type GetTabResponseInput,
  type CreateTabRequest,
  type CreateTabResponse,
  type CreateTabResponseInput,
  type EditTabRequest,
  type EditTabResponse,
  type EditTabResponseInput,
  type DeleteTabRequest,
  type DeleteTabResponse,
  type DeleteTabResponseInput,
  type GetAllCardsRequest,
  type GetAllCardsResponse,
  type GetAllCardsResponseInput,
  type GetCardRequest,
  type GetCardResponse,
  type GetCardResponseInput,
  type CreateCardRequest,
  type CreateCardResponse,
  type CreateCardResponseInput,
  type EditCardRequest,
  type EditCardResponse,
  type EditCardResponseInput,
  type DeleteCardRequest,
  type DeleteCardResponse,
  type DeleteCardResponseInput,
  type ViewCardRequest,
  type ViewCardResponse,
  type ViewCardResponseInput,
  GetTabResponseSchema
} from '../config/library.types'
import { handleError } from '@/utils/error.utils'
import type { ApiResponse } from '@/types/api.types'
import { unwrapResponse } from '@/utils/api.utils'
import * as v from '@/types/valibot.types'

const BASE_URL = '/web/api/infolibrary'
const MOBILE_BASE_URL = '/mobile/api/infolibrary'

/* =========================================================
   Tab API
========================================================= */

export async function getTabs(): Promise<GetTabsResponse> {
  const endpoint = `${BASE_URL}/gettabs`

  try {
    const { data } =
      await httpClient.get<ApiResponse<GetTabsResponseInput>>(endpoint)

    const raw = unwrapResponse<GetTabsResponseInput>(data)

    return v.parse(GetTabsResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'getTabs' })

    throw error
  }
}

export async function getTab(payload: GetTabRequest): Promise<GetTabResponse> {
  const endpoint = `${BASE_URL}/gettab`

  try {
    const parsedPayload = v.parse(GetTabRequestSchema, payload)

    const { data } = await httpClient.get<ApiResponse<GetTabResponseInput>>(
      endpoint,
      { params: parsedPayload }
    )

    const raw = unwrapResponse<GetTabResponseInput>(data)

    return v.parse(GetTabResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'getTab' })

    throw error
  }
}

export async function createTab(
  payload: CreateTabRequest
): Promise<CreateTabResponse> {
  const endpoint = `${BASE_URL}/createtab`

  try {
    const parsedPayload = v.parse(CreateTabRequestSchema, payload)

    const { data } = await httpClient.post<ApiResponse<CreateTabResponseInput>>(
      endpoint,
      parsedPayload
    )

    const raw = unwrapResponse<CreateTabResponseInput>(data)

    return v.parse(CreateTabResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'createTab' })

    throw error
  }
}

export async function editTab(
  payload: EditTabRequest
): Promise<EditTabResponse> {
  const endpoint = `${BASE_URL}/edittab`

  try {
    const parsedPayload = v.parse(EditTabRequestSchema, payload)

    const { data } = await httpClient.post<ApiResponse<EditTabResponseInput>>(
      endpoint,
      parsedPayload
    )

    const raw = unwrapResponse<EditTabResponseInput>(data)

    return v.parse(EditTabResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'editTab' })

    throw error
  }
}

export async function deleteTab(
  payload: DeleteTabRequest
): Promise<DeleteTabResponse> {
  const endpoint = `${BASE_URL}/deletetab`

  try {
    const parsedPayload = v.parse(DeleteTabRequestSchema, payload)

    const { data } = await httpClient.post<ApiResponse<DeleteTabResponseInput>>(
      endpoint,
      parsedPayload
    )

    const raw = unwrapResponse<DeleteTabResponseInput>(data)

    return v.parse(DeleteTabResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'deleteTab' })

    throw error
  }
}

/* =========================================================
   Card API
========================================================= */

export async function getAllCards(
  payload?: GetAllCardsRequest
): Promise<GetAllCardsResponse> {
  const endpoint = `${MOBILE_BASE_URL}/getallcards`

  try {
    const parsedPayload = v.parse(GetAllCardsRequestSchema, payload ?? {})

    const { data } = await httpClient.get<
      ApiResponse<GetAllCardsResponseInput>
    >(endpoint, { params: parsedPayload, headers: { 'api-version': '3' } })

    const raw = unwrapResponse<GetAllCardsResponseInput>(data)

    return v.parse(GetAllCardsResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'getAllCards' })

    throw error
  }
}

export async function getCard(
  payload: GetCardRequest
): Promise<GetCardResponse> {
  const endpoint = `${BASE_URL}/getcard`

  try {
    const parsedPayload = v.parse(GetCardRequestSchema, payload)

    const { data } = await httpClient.get<ApiResponse<GetCardResponseInput>>(
      endpoint,
      { params: parsedPayload }
    )

    const raw = unwrapResponse<GetCardResponseInput>(data)

    return v.parse(GetCardResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'getCard' })

    throw error
  }
}

export async function createCard(
  payload: CreateCardRequest
): Promise<CreateCardResponse> {
  const endpoint = `${BASE_URL}/createcard`

  try {
    const parsedPayload = v.parse(CreateCardRequestSchema, payload)

    const { data } = await httpClient.post<
      ApiResponse<CreateCardResponseInput>
    >(endpoint, parsedPayload)

    const raw = unwrapResponse<CreateCardResponseInput>(data)

    return v.parse(CreateCardResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'createCard' })

    throw error
  }
}

export async function editCard(
  payload: EditCardRequest
): Promise<EditCardResponse> {
  const endpoint = `${BASE_URL}/editcard`

  try {
    const parsedPayload = v.parse(EditCardRequestSchema, payload)

    const { data } = await httpClient.post<ApiResponse<EditCardResponseInput>>(
      endpoint,
      parsedPayload
    )

    const raw = unwrapResponse<EditCardResponseInput>(data)

    return v.parse(EditCardResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'editCard' })

    throw error
  }
}

export async function deleteCard(
  payload: DeleteCardRequest
): Promise<DeleteCardResponse> {
  const endpoint = `${BASE_URL}/deletecard`

  try {
    const parsedPayload = v.parse(DeleteCardRequestSchema, payload)

    const { data } = await httpClient.post<
      ApiResponse<DeleteCardResponseInput>
    >(endpoint, parsedPayload)

    const raw = unwrapResponse<DeleteCardResponseInput>(data)

    return v.parse(DeleteCardResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'deleteCard' })

    throw error
  }
}

export async function viewCard(
  payload: ViewCardRequest
): Promise<ViewCardResponse> {
  const endpoint = `${BASE_URL}/viewcard`

  try {
    const parsedPayload = v.parse(ViewCardRequestSchema, payload)

    const { data } = await httpClient.post<ApiResponse<ViewCardResponseInput>>(
      endpoint,
      parsedPayload
    )

    const raw = unwrapResponse<ViewCardResponseInput>(data)

    return v.parse(ViewCardResponseSchema, raw)
  } catch (error) {
    handleError({ error, action: 'viewCard' })

    throw error
  }
}
